import type React from "react";

import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

interface ISpinnerProps {
    size?: number | string;
    color?: string;
    text?: string;
    className?: string;
    speed?: number; // rotation speed in seconds
    thickness?: number | string; // thickness of the spinner
    testId?: string; // for testing purposes
}

// Using a regular div instead of Flex to avoid issues
const SpinnerContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 12px;
`;

const SpinnerElement = styled.div<{
    $size: string | number;
    $color: string;
    $speed: number;
    $thickness: string | number;
}>`
    box-sizing: border-box;
    width: ${({ $size }) => (typeof $size === "number" ? `${$size}px` : $size)};
    height: ${({ $size }) => (typeof $size === "number" ? `${$size}px` : $size)};
    border: ${({ $thickness }) => (typeof $thickness === "number" ? `${$thickness}px` : $thickness)} solid
        rgba(0, 0, 0, 0.1);
    border-top-color: ${({ $color }) => $color};
    border-radius: 100%;
    animation: spin ${({ $speed }) => $speed}s linear infinite;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

export const Spinner: React.FC<ISpinnerProps> = ({
    size = 32,
    color = colorsPalettes.blue[400],
    text,
    className,
    speed = 0.8,
    thickness = 3,
    testId = "spinner",
}) => {
    return (
        <SpinnerContainer className={className}>
            <SpinnerElement
                $size={size}
                $color={color}
                $speed={speed}
                $thickness={thickness}
                data-testid={testId}
            />
            {text ? <Text fontVariant="subtitle1">{text}</Text> : null}
        </SpinnerContainer>
    );
};
